import { supabase } from '../supabase';

export interface AIExcelExample {
  id: string;
  receiver_name: string;
  exporter_id: number | null;
  species_id: number | null;
  filename: string;
  ai_output: any;
  storage_path: string | null;
  source: 'no_robot' | 'robot_failed' | 'no_match';
  status: 'pending' | 'approved' | 'rejected';
  notes: string | null;
  created_at: string;
}

export interface ReceiverGroup {
  receiver_name: string;
  examples: AIExcelExample[];
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  sources: { no_robot: number; robot_failed: number; no_match: number };
}

export const getAIExcelExamples = async (): Promise<ReceiverGroup[]> => {
  const { data, error } = await supabase
    .from('ai_excel_examples')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  const grouped: Record<string, AIExcelExample[]> = {};
  for (const row of data || []) {
    if (!grouped[row.receiver_name]) {
      grouped[row.receiver_name] = [];
    }
    grouped[row.receiver_name].push(row);
  }

  return Object.entries(grouped)
    .map(([receiver_name, examples]) => ({
      receiver_name,
      examples,
      total: examples.length,
      pending: examples.filter((e) => e.status === 'pending').length,
      approved: examples.filter((e) => e.status === 'approved').length,
      rejected: examples.filter((e) => e.status === 'rejected').length,
      sources: {
        no_robot: examples.filter((e) => e.source === 'no_robot').length,
        robot_failed: examples.filter((e) => e.source === 'robot_failed').length,
        no_match: examples.filter((e) => e.source === 'no_match').length,
      },
    }))
    .sort((a, b) => b.total - a.total);
};

export const updateExampleStatus = async (
  id: string,
  status: 'pending' | 'approved' | 'rejected',
  notes?: string
): Promise<void> => {
  const update: Record<string, any> = { status };
  if (notes !== undefined) update.notes = notes;

  const { error } = await supabase
    .from('ai_excel_examples')
    .update(update)
    .eq('id', id);

  if (error) throw error;
};

export const deleteExample = async (id: string, storagePath?: string | null): Promise<void> => {
  if (storagePath) {
    await supabase.storage.from('excel-examples').remove([storagePath]);
  }

  const { error } = await supabase
    .from('ai_excel_examples')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const downloadExampleFile = async (storagePath: string): Promise<Blob> => {
  const { data, error } = await supabase.storage
    .from('excel-examples')
    .download(storagePath);

  if (error) throw error;
  if (!data) throw new Error('No data returned from storage');

  return data;
};
